"use client"

import { Pencil, Trash2, Plus, FileText } from "lucide-react"
import { useEffect, useState } from "react"
import { Button } from "../../../components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card"
import { ScrollArea } from "../../../components/ui/scroll-area"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table"
import { useSearchParams } from "react-router-dom"
import { convertParamsToJson, useCashfitParams } from "@/view/hooks/use-params"
import { useRemoteClientPost, useRemoteClientDeleteWithoutId } from "@/configs/http-client"
import { useLocalStorage } from "@/remote/local-storage"
import { TrainingSheetButton } from "./button-print-exercise"
import { CatchMessageError } from "@/remote/catch-message-error"
import { useToast } from "../../../hooks"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog"

interface WorkoutListProps {
  changeTab: (tab: string) => void,
}

export function WorkoutList({ changeTab }: WorkoutListProps) {
  const [params] = useSearchParams()
  const [workouts, setWorkouts] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [workoutToDelete, setWorkoutToDelete] = useState<number | null>(null)
  const [sheetInfo, setSheetInfo] = useState<any>(null)

  const { getParam, setParams } = useCashfitParams(convertParamsToJson(params))
  const { getUnitId } = useLocalStorage()
  const { httpClientPost } = useRemoteClientPost()
  const { remoteClientDelete } = useRemoteClientDeleteWithoutId()
  const { showToast } = useToast()
  const sheetId = getParam("sheetId")
  const nameStudant = getParam("nameStudent") || ""

  const fetchWorkouts = async () => {
    if (!sheetId) {
      setWorkouts([])
      return
    }

    setLoading(true)
    try {
      const response = await httpClientPost(`/fichas-treino/filtro?page=${0}&size=1000`, {
        idUnidade: getUnitId(),
        idFicha: sheetId,
      })

      if (response.data?.data?.content) {
        setWorkouts(response.data.data.content)
        if (response.data.data.content.length > 0) {
          const firstWorkout = response.data.data.content[0]
          if (firstWorkout.ficha) {
            setSheetInfo(firstWorkout.ficha)
          }
        }
      } else {
        setWorkouts([])
      }
    } catch (error) {
      CatchMessageError(error)
      setWorkouts([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchWorkouts()
  }, [sheetId])

  const handleEditWorkout = (workoutId: number) => {
    setParams({ traningId: workoutId.toString() })
    changeTab("add-workout")
  }

  const confirmDeleteWorkout = (workoutId: number) => {
    setWorkoutToDelete(workoutId)
    setDeleteDialogOpen(true)
  }

  const handleDeleteWorkout = async () => {
    if (!workoutToDelete) return

    try {
      await remoteClientDelete(`/fichas-treino/${workoutToDelete}?idUnidade=${getUnitId()}`)
      showToast("Treino excluído com sucesso", "success")
      fetchWorkouts()
    } catch (error) {
      CatchMessageError(error)
    } finally {
      setWorkoutToDelete(null)
      setDeleteDialogOpen(false)
    }
  }

  const handleAddWorkout = () => {
    if (sheetId) {
      setParams({ sheetId, traningId: "" })
    }
    changeTab("add-workout")
  }

  return (
    <Card className="w-full">
      <CardHeader className="pb-2">
        <div className="flex justify-between items-center">
          <CardTitle>{sheetId && sheetInfo ? `Treinos para ${sheetInfo.nome}` : "Todos os Treinos"}</CardTitle>
          {/* <Button
            onClick={handleAddWorkout}
            className="bg-acelerafit-green-400 text-white hover:bg-acelerafit-green-300"
          >
            <Plus className="mr-2 h-4 w-4" />
            Novo Treino
          </Button> */}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center items-center h-40">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-acelerafit-green-400"></div>
          </div>
        ) : workouts.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900">
              {sheetId ? "Nenhum treino encontrado" : "Selecione uma ficha para visualizar os treinos"}
            </h3>
            {sheetId && (
              <>
                <p className="mt-1 text-sm text-gray-500">Comece criando um novo treino.</p>
                <div className="mt-6">
                  <Button
                    onClick={handleAddWorkout}
                    className="bg-acelerafit-green-400 text-white hover:bg-acelerafit-green-300"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Novo Treino
                  </Button>
                </div>
              </>
            )}
          </div>
        ) : (
          <ScrollArea className="h-[400px] w-full">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead>Exercícios</TableHead>
                  <TableHead>Unidade</TableHead>
                  <TableHead className="w-[80px] text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {workouts.map((workout) => (
                  <TableRow key={workout.id}>
                    <TableCell>{workout.id}</TableCell>
                    <TableCell className="font-medium">{workout.nome}</TableCell>
                    <TableCell>{workout.exercicios?.length || 0}</TableCell>
                    <TableCell>{workout.unidade?.nomeFantasia}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditWorkout(workout.id)}
                          className="h-8 w-8 p-0"
                          title="Editar"
                        >
                          <Pencil className="h-4 w-4 text-blue-500" />
                        </Button>
                        <div className="h-8 w-8 flex items-center justify-center">
                          <TrainingSheetButton nameStudant={nameStudant} idTreino={workout.id} idUnidade={getUnitId()} />
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => confirmDeleteWorkout(workout.id)}
                          className="h-8 w-8 p-0"
                          title="Excluir"
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        )}
      </CardContent>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Tem certeza?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não poderá ser desfeita. Isso excluirá permanentemente o treino e todos os exercícios
              associados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteWorkout} className="bg-red-600 text-white hover:bg-red-700">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  )
}
